import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import QuickActionButton from '../../components/ui/QuickActionButton';
import Button from '../../components/ui/Button';
import WeeklyCalendar from './components/WeeklyCalendar';
import FilterBar from './components/FilterBar';
import ClassCard from './components/ClassCard';
import UpcomingBookings from './components/UpcomingBookings';
import ClassDetailsModal from './components/ClassDetailsModal';
import { emailService } from '../../services/emailService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const ClassSchedule = () => {
  const navigate = useNavigate();
  const { user, userProfile, currentTenant } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedView, setSelectedView] = useState('grid');
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [bookedClassIds, setBookedClassIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    difficulty: 'all',
    time: 'all',
    instructor: 'all'
  });

  const allClasses = [
  {
    id: 1,
    name: "Power Yoga Flow",
    instructor: "Sarah Johnson",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ae819c95-1763294232847.png",
    instructorImageAlt: "Professional female yoga instructor with long brown hair in athletic wear smiling warmly at camera",
    instructorBio: "Certified yoga instructor with 10+ years of experience specializing in power yoga and mindfulness practices.",
    date: "2026-01-24",
    time: "6:00 AM",
    duration: 60,
    difficulty: "Intermediate",
    spotsLeft: 5,
    totalSpots: 20,
    category: "Yoga",
    categoryColor: "#9333EA",
    location: "Studio A",
    description: "Start your day with an energizing power yoga session that combines strength, flexibility, and mindfulness. This dynamic flow will challenge your body while calming your mind, perfect for intermediate practitioners looking to deepen their practice.",
    equipment: ["Yoga Mat", "Blocks", "Strap"],
    benefits: [
    "Improves flexibility and core strength",
    "Enhances mental clarity and focus",
    "Reduces stress and anxiety",
    "Builds lean muscle tone"],

    reviews: [
    { id: 1, memberName: "Jessica M.", rating: 5, comment: "Sarah\'s classes are amazing! Perfect balance of challenge and relaxation." },
    { id: 2, memberName: "Robert K.", rating: 5, comment: "Best way to start the morning. Highly recommend!" },
    { id: 3, memberName: "Amanda L.", rating: 4, comment: "Great class, very energizing. Would love more advanced variations." }]

  },
  {
    id: 2,
    name: "HIIT Cardio Blast",
    instructor: "Mike Chen",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_12725a579-1763294235596.png",
    instructorImageAlt: "Athletic male fitness trainer with short black hair wearing red tank top demonstrating exercise form",
    instructorBio: "Former professional athlete turned fitness coach, specializing in high-intensity interval training and metabolic conditioning.",
    date: "2026-01-24",
    time: "7:30 AM",
    duration: 45,
    difficulty: "Advanced",
    spotsLeft: 2,
    totalSpots: 15,
    category: "HIIT",
    categoryColor: "#DC2626",
    location: "Studio B",
    description: "High-intensity interval training designed to maximize calorie burn and boost metabolism. This fast-paced workout combines cardio bursts with strength exercises for a complete full-body challenge.",
    equipment: ["None Required"],
    benefits: [
    "Burns maximum calories in minimal time",
    "Increases cardiovascular endurance",
    "Boosts metabolism for hours after workout",
    "Improves athletic performance"],

    reviews: [
    { id: 1, memberName: "David S.", rating: 5, comment: "Intense but worth it! Mike pushes you to your limits." },
    { id: 2, memberName: "Emily R.", rating: 5, comment: "Best cardio workout I've ever done. Results speak for themselves!" },
    { id: 3, memberName: "Chris P.", rating: 4, comment: "Very challenging but Mike provides great modifications." }]

  },
  {
    id: 3,
    name: "Strength & Conditioning",
    instructor: "Emma Rodriguez",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1764f1e5c-1763299401492.png",
    instructorImageAlt: "Strong female trainer with pulled back dark hair in black athletic wear holding dumbbells with confident expression",
    instructorBio: "Certified strength and conditioning specialist with expertise in functional fitness and Olympic lifting techniques.",
    date: "2026-01-24",
    time: "9:00 AM",
    duration: 60,
    difficulty: "Intermediate",
    spotsLeft: 8,
    totalSpots: 18,
    category: "Strength Training",
    categoryColor: "#2563EB",
    location: "Weight Room",
    description: "Build strength and muscle with this comprehensive resistance training session. Focus on compound movements and progressive overload to achieve your strength goals safely and effectively.",
    equipment: ["Dumbbells", "Barbell", "Resistance Bands", "Bench"],
    benefits: [
    "Increases muscle mass and strength",
    "Improves bone density",
    "Enhances functional fitness",
    "Boosts metabolism and fat burning"],

    reviews: [
    { id: 1, memberName: "Marcus T.", rating: 5, comment: "Emma\'s programming is excellent. Seeing great strength gains!" },
    { id: 2, memberName: "Sarah W.", rating: 5, comment: "Perfect mix of challenging and achievable. Love this class!" },
    { id: 3, memberName: "Tom H.", rating: 5, comment: "Best strength class at the gym. Emma knows her stuff!" }]

  },
  {
    id: 4,
    name: "Beginner Pilates",
    instructor: "Lisa Anderson",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1641a596b-1763294716225.png",
    instructorImageAlt: "Graceful female pilates instructor with blonde hair in gray athletic outfit demonstrating core exercise on mat",
    instructorBio: "Certified Pilates instructor specializing in rehabilitation and core strengthening for all fitness levels.",
    date: "2026-01-24",
    time: "10:30 AM",
    duration: 50,
    difficulty: "Beginner",
    spotsLeft: 12,
    totalSpots: 16,
    category: "Pilates",
    categoryColor: "#059669",
    location: "Studio A",
    description: "Perfect introduction to Pilates focusing on core strength, flexibility, and body awareness. Learn fundamental movements and breathing techniques in a supportive environment.",
    equipment: ["Pilates Mat", "Small Ball", "Resistance Band"],
    benefits: [
    "Strengthens core muscles",
    "Improves posture and alignment",
    "Increases flexibility",
    "Reduces back pain"],

    reviews: [
    { id: 1, memberName: "Jennifer B.", rating: 5, comment: "Lisa is so patient and explains everything clearly. Perfect for beginners!" },
    { id: 2, memberName: "Michael D.", rating: 5, comment: "Never thought I\'d enjoy Pilates but this class changed my mind!" },
    { id: 3, memberName: "Rachel G.", rating: 4, comment: "Great class for learning the basics. Would love more challenging variations." }]

  },
  {
    id: 5,
    name: "Spin Cycle",
    instructor: "David Kim",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_16a98608d-1763295470113.png",
    instructorImageAlt: "Energetic male cycling instructor with short dark hair in bright blue cycling jersey with enthusiastic smile",
    instructorBio: "Professional cycling coach and endurance athlete with passion for indoor cycling and music-driven workouts.",
    date: "2026-01-24",
    time: "12:00 PM",
    duration: 45,
    difficulty: "Intermediate",
    spotsLeft: 3,
    totalSpots: 25,
    category: "Cycling",
    categoryColor: "#F59E0B",
    location: "Cycling Studio",
    description: "High-energy indoor cycling class set to motivating music. Experience hill climbs, sprints, and endurance rides that will challenge your cardiovascular fitness and leg strength.",
    equipment: ["Cycling Shoes (Optional)", "Water Bottle", "Towel"],
    benefits: [
    "Excellent cardiovascular workout",
    "Low-impact on joints",
    "Burns significant calories",
    "Builds leg strength and endurance"],

    reviews: [
    { id: 1, memberName: "Kelly F.", rating: 5, comment: "David\'s energy is contagious! Best spin class ever!" },
    { id: 2, memberName: "Brian M.", rating: 5, comment: "Great music selection and challenging intervals. Never boring!" },
    { id: 3, memberName: "Nicole P.", rating: 5, comment: "Perfect lunchtime workout. Always leave feeling accomplished!" }]

  },
  {
    id: 6,
    name: "Restorative Yoga",
    instructor: "Sarah Johnson",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ae819c95-1763294232847.png",
    instructorImageAlt: "Professional female yoga instructor with long brown hair in athletic wear smiling warmly at camera",
    instructorBio: "Certified yoga instructor with 10+ years of experience specializing in power yoga and mindfulness practices.",
    date: "2026-01-24",
    time: "5:30 PM",
    duration: 60,
    difficulty: "Beginner",
    spotsLeft: 10,
    totalSpots: 20,
    category: "Yoga",
    categoryColor: "#9333EA",
    location: "Studio A",
    description: "Gentle yoga practice focused on relaxation and stress relief. Perfect for unwinding after a long day with supported poses and deep breathing exercises.",
    equipment: ["Yoga Mat", "Bolster", "Blanket", "Blocks"],
    benefits: [
    "Reduces stress and anxiety",
    "Improves sleep quality",
    "Promotes deep relaxation",
    "Enhances flexibility gently"],

    reviews: [
    { id: 1, memberName: "Patricia L.", rating: 5, comment: "So relaxing! Perfect way to end the day." },
    { id: 2, memberName: "James W.", rating: 5, comment: "Helps me sleep better. Sarah creates such a peaceful atmosphere." },
    { id: 3, memberName: "Linda S.", rating: 5, comment: "Exactly what I needed. Highly recommend for stress relief!" }]

  },
  {
    id: 7,
    name: "Boxing Fundamentals",
    instructor: "Mike Chen",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_12725a579-1763294235596.png",
    instructorImageAlt: "Athletic male fitness trainer with short black hair wearing red tank top demonstrating exercise form",
    instructorBio: "Former professional athlete turned fitness coach, specializing in high-intensity interval training and metabolic conditioning.",
    date: "2026-01-25",
    time: "6:30 AM",
    duration: 60,
    difficulty: "Beginner",
    spotsLeft: 6,
    totalSpots: 12,
    category: "Cardio",
    categoryColor: "#DC2626",
    location: "Studio B",
    description: "Learn proper boxing techniques including stance, footwork, and basic combinations. Great cardio workout while building coordination and confidence.",
    equipment: ["Boxing Gloves", "Hand Wraps", "Water Bottle"],
    benefits: [
    "Full-body cardio workout",
    "Improves coordination and reflexes",
    "Builds upper body strength",
    "Great stress relief"],

    reviews: [
    { id: 1, memberName: "Alex M.", rating: 5, comment: "Mike is an excellent teacher. Learning so much!" },
    { id: 2, memberName: "Sophia R.", rating: 5, comment: "Fun and challenging! Great workout and skill building." },
    { id: 3, memberName: "Daniel K.", rating: 4, comment: "Really enjoying learning boxing. Wish it was longer!" }]

  },
  {
    id: 8,
    name: "Core Power",
    instructor: "Emma Rodriguez",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1764f1e5c-1763299401492.png",
    instructorImageAlt: "Strong female trainer with pulled back dark hair in black athletic wear holding dumbbells with confident expression",
    instructorBio: "Certified strength and conditioning specialist with expertise in functional fitness and Olympic lifting techniques.",
    date: "2026-01-25",
    time: "8:00 AM",
    duration: 30,
    difficulty: "Intermediate",
    spotsLeft: 0,
    totalSpots: 15,
    category: "Strength Training",
    categoryColor: "#2563EB",
    location: "Studio A",
    description: "Focused 30-minute core workout targeting abs, obliques, and lower back. Build a strong foundation for all your fitness activities.",
    equipment: ["Mat", "Medicine Ball", "Resistance Band"],
    benefits: [
    "Strengthens entire core",
    "Improves balance and stability",
    "Enhances athletic performance",
    "Reduces risk of injury"],

    reviews: [
    { id: 1, memberName: "Ryan T.", rating: 5, comment: "Short but intense! Feel the burn every time." },
    { id: 2, memberName: "Michelle C.", rating: 5, comment: "Perfect quick core workout. Emma is amazing!" },
    { id: 3, memberName: "Kevin L.", rating: 5, comment: "30 minutes of pure core work. Love it!" }]

  }];


  const upcomingBookings = useMemo(() => {
    return allClasses?.filter((cls) => bookedClassIds?.includes(cls?.id))?.map((cls) => ({
      ...cls,
      date: new Date(cls.date)?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    }));
  }, [bookedClassIds]);

  const filteredClasses = useMemo(() => {
    return allClasses?.filter((cls) => {
      const classDate = new Date(cls.date);
      const matchesDate = classDate?.toDateString() === selectedDate?.toDateString();

      const matchesCategory = filters?.category === 'all' ||
      cls?.category?.toLowerCase()?.includes(filters?.category);

      const matchesDifficulty = filters?.difficulty === 'all' ||
      cls?.difficulty === filters?.difficulty;

      const matchesTime = filters?.time === 'all' || (() => {
        const hour = parseInt(cls?.time?.split(':')?.[0]);
        const isPM = cls?.time?.includes('PM');
        const hour24 = isPM && hour !== 12 ? hour + 12 : hour;

        if (filters?.time === 'morning') return hour24 >= 6 && hour24 < 12;
        if (filters?.time === 'afternoon') return hour24 >= 12 && hour24 < 17;
        if (filters?.time === 'evening') return hour24 >= 17 && hour24 < 21;
        return true;
      })();

      const matchesInstructor = filters?.instructor === 'all' ||
      cls?.instructor?.toLowerCase()?.includes(filters?.instructor);

      return matchesDate && matchesCategory && matchesDifficulty && matchesTime && matchesInstructor;
    });
  }, [selectedDate, filters]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      category: 'all',
      difficulty: 'all',
      time: 'all',
      instructor: 'all'
    });
  };

  const handleBookClass = async (classId) => {
    if (!user || !userProfile || !currentTenant) {
      alert('Please log in to book a class');
      return;
    }

    setLoading(true);
    try {
      const classToBook = allClasses?.find((c) => c?.id === classId);
      if (!classToBook) {
        throw new Error('Class not found');
      }

      // Create booking in database
      const { data: booking, error: bookingError } = await supabase?.from('class_bookings')?.insert({
        class_id: classId,
        user_id: user?.id,
        status: 'confirmed'
      })?.select()?.single();

      if (bookingError) throw bookingError;

      // Update local state
      setBookedClassIds([...bookedClassIds, classId]);

      // Send booking confirmation email
      const emailResult = await emailService?.sendBookingConfirmation({
        memberEmail: userProfile?.email || user?.email,
        memberName: userProfile?.full_name || 'Member',
        className: classToBook?.name,
        classTime: `${classToBook?.date} at ${classToBook?.time}`,
        instructorName: classToBook?.instructor,
        duration: classToBook?.duration,
        location: classToBook?.location || 'Main Studio',
        tenantName: currentTenant?.name || 'Gym'
      });

      if (emailResult?.success) {
        alert(`Class booked successfully! Confirmation email sent to ${userProfile?.email || user?.email}`);
      } else {
        alert('Class booked successfully! (Email notification failed)');
      }
    } catch (error) {
      console.error('Booking error:', error);
      alert(error?.message || 'Failed to book class. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = (classId) => {
    setBookedClassIds((prev) => prev?.filter((id) => id !== classId));
  };

  const handleViewDetails = (classId) => {
    setSelectedClassId(classId);
  };

  const selectedClassData = allClasses?.find((cls) => cls?.id === selectedClassId);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-[1440px] px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10 pb-24 lg:pb-10">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-2">
                Class Schedule
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Book your favorite fitness classes and manage your schedule
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant={selectedView === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedView('grid')}
                iconName="Grid3x3"
                iconSize={18}
                className="flex-1 md:flex-none"
                aria-label="Grid view">

                Grid
              </Button>
              <Button
                variant={selectedView === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedView('list')}
                iconName="List"
                iconSize={18}
                className="flex-1 md:flex-none"
                aria-label="List view">

                List
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-6 md:space-y-8">
            <WeeklyCalendar
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              classesData={allClasses} />


            <FilterBar
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters} />


            <div>
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                  Available Classes
                </h2>
                <span className="text-sm md:text-base text-muted-foreground">
                  {filteredClasses?.length} {filteredClasses?.length === 1 ? 'class' : 'classes'}
                </span>
              </div>

              {filteredClasses?.length === 0 ?
              <div className="bg-card border border-border rounded-lg shadow-md p-8 md:p-12 lg:p-16">
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-muted rounded-full flex items-center justify-center mb-4">
                      <Icon name="Calendar" size={40} color="var(--color-muted-foreground)" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
                      No Classes Available
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground max-w-md mb-6">
                      There are no classes matching your filters for the selected date. Try adjusting your filters or selecting a different date.
                    </p>
                    <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    iconName="RefreshCw"
                    iconPosition="left">

                      Clear All Filters
                    </Button>
                  </div>
                </div> :

              <div className={`
                  ${selectedView === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6' : 'space-y-4 md:space-y-6'}
                `
              }>
                  {filteredClasses?.map((classData) =>
                <ClassCard
                  key={classData?.id}
                  classData={classData}
                  onBook={handleBookClass}
                  onViewDetails={handleViewDetails}
                  isBooked={bookedClassIds?.includes(classData?.id)} />

                )}
                </div>
              }
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-6">
              <UpcomingBookings
                bookings={upcomingBookings}
                onCancel={handleCancelBooking}
                onViewDetails={handleViewDetails} />


              <div className="bg-card border border-border rounded-lg shadow-md p-4 md:p-5 lg:p-6">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Info" size={20} color="var(--color-primary)" />
                  Quick Tips
                </h3>
                <ul className="space-y-3 text-sm text-foreground">
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <span>Arrive 10 minutes early for your first class</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <span>Bring water and a towel to all classes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <span>Cancel at least 2 hours before class time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <span>Check equipment requirements before booking</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
      <MobileBottomNav />
      <QuickActionButton />
      {selectedClassData &&
      <ClassDetailsModal
        classData={selectedClassData}
        onClose={() => setSelectedClassId(null)}
        onBook={handleBookClass}
        isBooked={bookedClassIds?.includes(selectedClassId)} />

      }
    </div>);

};

export default ClassSchedule;